import {
  Box,
  CircularProgress,
  Tooltip,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { InnerButton } from "../../../../../../shared/components/buttons/floating-button-container";
import {
  downloadDocx,
  DownloadDocxOptions,
} from "../../../../../../shared/components/document/docx-downloader";
import FormBuilderV4 from "../../../../../../shared/components/form-builder/form-builder-v4";
import { FormConfigProps } from "../../../../../../shared/components/form-builder/form-builder.model";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import ViewerModalPopup from "../../../../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../../../../shared/components/preview/pdf-viewer";
import { useApi } from "../../../../../../shared/hooks/useApi";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import { Cancel } from "@mui/icons-material";

// Data structure for claims categories
interface ClaimsCategoryData {
  claimsCategories: string[];
}
// Data structure for claims categories
interface SCProductComponetData {
  scProductComponentCode: string[];
}

// Define the interface for API Response
export interface ComponentDetailsResponse {
  scId: string;
  componentId: string;
  scProductComponentCode: string;
  scComponentDetail: string;
  scProductDetailCode: string;
}

export interface ComponentDetailsApiResponse {
  scId: string;
  scProductComponentCode: string[];
}

export interface ProductClaimsResponse {
  scId: string;
  scProductClaimsCategory: string[];
}

interface ComponentClaimsCategoryDetailsTabProps {
  scStatus?: boolean;
}

const ComponentClaimsCategoryDetailsTab: React.FC<
  ComponentClaimsCategoryDetailsTabProps
> = ({ scStatus }) => {
  const navigate = useNavigate();
  const { SC_ID, formtype } = useParams();
  const formRef = useRef<any>(null);
  const componentInfoFormRef = useRef<any>(null);
  const { executePost, loading: isSaving } = useApi();
  const { executeGet, executeDelete, loading: isLoading } = useApi();

  // State for component information table
  const [componentData, setComponentData] = useState<string[]>([]);
  const [claimsData, setClaimsData] = useState<string[]>([]);
  const [componentCodeOptions, setComponentCodeOptions] = useState<any[]>([]);
  const [scClaimsCodeOptions, setScClaimsCodeOptions] = useState<any[]>([]);
  const [scStandards, setScStandards] = useState<string[]>([]);

  // State for claims categories form
  const [claimsCategoryData, setClaimsCategoryData] = useState<string[]>([]);

  // Button states
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { isRestrictPopUpSave, setRestrictPopUpSave, cbLicensingCode } =
    useTransactionCertificateStore();
  const { notify } = useNotification();

  const hasSubmitSC: boolean = formtype === "submited-sc";
  const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;

  // Fetch standards from SC Details API
  const fetchStandardsFromSCDetails = async () => {
    if (!SC_ID) return;
    
    try {
      const result = await executeGet(API_ROUTES.GET_SC_DETAILS_V4, { scId: SC_ID });
      const standards = result?.data?.scStandardInformation?.map(
        (item: any) => item.scStandard
      ) || [];
      
      setScStandards(standards);
    } catch (error) {
      console.error("Error fetching SC standards:", error);
      notify("Error fetching standards", "error");
    }
  };

  const loadInitialValues = async() =>{
    await getComponentDetails();
    getProductClaims();
    await getComponentCodes();
    getSCClaimsCodes();
    fetchStandardsFromSCDetails();
  }

  // Fetch data on page load
  useEffect(() => {
    loadInitialValues();
  }, [SC_ID]);

  // Fetch existing component details from API
  const getComponentDetails = async () => {
    if (!SC_ID) return;

    try {
      const response = await executeGet(API_ROUTES.GET_COMPONENT_DETAILS_V4, {
        scId: SC_ID,
      });

      if (response?.status === 200 && response.data) {
        const apiData: any = response.data;

        if (apiData.scProductComponentCode) {
          // Transform API response to component data format
          const transformedData: string[] = apiData?.scProductComponentCode;
          setComponentData(
            transformedData.map((item) => item.split(";")[0].trim())
          );
        }
      }
    } catch (error) {
      console.error("Error fetching component details:", error);
      notify("Error fetching component details", "error");
    }
  };

  // Fetch existing product claims from API
  const getProductClaims = async () => {
    if (!SC_ID) return;

    try {
      const response = await executeGet(
        API_ROUTES.GET_PRODUCT_CLAIMS_DETAILS_V4,
        { scId: SC_ID }
      );

      if (response?.status === 200 && response.data) {
        const apiData: ProductClaimsResponse = response.data;

        if (apiData.scProductClaimsCategory) {
          setClaimsCategoryData(apiData.scProductClaimsCategory);
        }
      }
    } catch (error) {
      console.error("Error fetching product claims:", error);
      notify("Error fetching product claims", "error");
    }
  };

  // Fetch component codes from API
  const getComponentCodes = async () => {
    try {
      const queryParams = {
        query_param: TCAppConfig.GET_PRODUCT_DETAIL_CODE,
      };
      const response = await executeGet(
        TCAppConfig.DTRACKIT_BASE_ENDPOINT,
        queryParams
      );
      if (response?.status === 200 && response.data) {
        const options = response.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1 ?? ""} (${t.code_1})`,
          code: `${t.code_1}; ${t.description_1}`,
        }));

        // Add NA option
        const naOption = {
          value: "na",
          label: "na",
          code: "na",
        };

        setComponentCodeOptions([...options, naOption]);
      }
    } catch (err) {
      console.error("Error in Fetching Component Codes:", err);
      notify("Error fetching component codes", "error");
    }
  };
  // Fetch SC Claims codes from API using v4 service
  const getSCClaimsCodes = async () => {
    try {
      const response =
        await dtrackItServiceV4.getCertifiedOrganizationApprovedClaimsValuesLG();
      if (response?.data) {
        const options = response.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1}`,
          code: t.code_1,
        }));
        setScClaimsCodeOptions(options);
      }
    } catch (err) {
      console.error("Error in Fetching SC Claims Codes:", err);
      notify("Error fetching SC claims codes", "error");
    }
  };

  // Button handlers following facility-product-details pattern
  const handleSCSave = async () => {
    try {
      if (!componentData || _.isEmpty(componentData)) {
        notify("Please add component information details", "error");
        return;
      }
      if (!claimsCategoryData || _.isEmpty(claimsCategoryData)) {
        notify("Please add SC Claims information details", "error");
        return;
      }

      // Submit the form to save claims categories first
      if (componentInfoFormRef.current) {
        await componentInfoFormRef.current.submitForm();
      }
      // Submit the form to save claims categories first
      if (formRef.current) {
        await formRef.current.submitForm();
      }
      //componentInfoFormConfig
      // Save scope certificate data
      const params = {
        scId: SC_ID,
        scCblicensingCode: cbLicensingCode,
      };
      const result: any = await executePost(API_ROUTES.SAVE_SC_V4, params);
      if (result.status === 200) {
        notify("Saved Successfully", "success");
        setIsSubmitted(false);
      } else {
        notify("Unable to Save", "error");
      }
    } catch (error) {
      console.error("Error saving Component & Claims Category Details:", error);
      notify("Error saving Component & Claims Category Details", "error");
    }
  };

  const handleSCSubmit = async () => {
    try {
      if (!componentData || _.isEmpty(componentData)) {
        notify("Please add component information details", "error");
        return;
      }
      if (!claimsCategoryData) {
        notify("Please add claims categories", "error");
        return;
      }

      const params = {
        scId: SC_ID,
      };
      const result: any = await executePost(API_ROUTES.SUBMIT_SC_V4, params);
      if (result.status === 200) {
        notify("Submitted Successfully", "success");
        setIsSubmitted(false);
      } else {
        notify("Unable to Submit", "error");
      }
    } catch (error) {
      console.error(
        "Error submitting Component & Claims Category Details:",
        error
      );
      notify("Error submitting Component & Claims Category Details", "error");
    }
  };

  const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

  // Filter claim codes based on selected standards
  const filteredScClaimsCodeOptions = useMemo(() => {
    if (!scStandards.length || !scClaimsCodeOptions.length) {
      return scClaimsCodeOptions;
    }

    return scClaimsCodeOptions.filter((option: any) => {
      // Check if the claim code starts with any of the standard codes
      return scStandards.some((standard: string) => 
        option.code.startsWith(standard)
      );
    });
  }, [scStandards, scClaimsCodeOptions]);

  const handleDownload = async () => {
    setIsDownloading(true);
    const options: DownloadDocxOptions = {
      apiUrl: API_ROUTES.GET_WORD_DOC_V4,
      params: {
        scId: SC_ID ?? "",
        cbCode: cbLicensingCode,
      },
      headers: {
        // Optional headers if needed
      },
    };

    try {
      notify("Starting Download", "info");
      await downloadDocx(options);
      notify("File Downloaded", "success");
      setIsDownloading(false);
    } catch (error) {
      notify("Failed to download DOCX", "error");
      setIsDownloading(false);
    }
  };

  // Form configuration for claims categories
  const formConfig: FormConfigProps = {
    initialValues: claimsCategoryData,
    validationSchema: Yup.object().shape({
      claimsCategories: Yup.array().min(
        1,
        "At least one claims category is required"
      ),
    }),
    readOnly: isReadOnlyBasedOnStatus,
    sections: [
      {
        // title: " SC Product Claims Category",
        fields: [
          {
            name: "claimsCategories",
            label: "SC Product Claims Category",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 12 },
            events: {
              onChange: async (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                setClaimsCategoryData(updatedValue);
              },
            },
            props: {
              placeholder: "Select SC Product Claims Category",
              options: filteredScClaimsCodeOptions,
              multiple: true,
              enableSearch: true,
              value: claimsCategoryData,
              width: "600px",
            },
          },
        ],
      },
    ],
    pageName: "",
    handleSubmit: async (values: ClaimsCategoryData) => {
      try {
        if (!claimsCategoryData || _.isEmpty(claimsCategoryData)) {
          notify("Please add claims details", "error");
          return false;
        }

        // Save the claims categories first
        const claimsParams = {
          scId: SC_ID,
          scProductClaimsCategory: claimsCategoryData,
        };

        const claimsResult: any = await executePost(
          API_ROUTES.POST_PRODUCT_CLAIMS_DETAILS_V4,
          claimsParams
        );

        if (claimsResult.status === 200 || claimsResult.status === 201) {
          notify("Claims categories saved successfully", "success");
          return true;
        } else {
          notify("Failed to save claims categories", "error");
          return false;
        }
      } catch (error) {
        console.error("Error saving claims categories:", error);
        notify("Error saving claims categories", "error");
        return false;
      }
    },
  };
  console.log(isReadOnlyBasedOnStatus, "isReadOnlyBasedOnStatus");

  // Form configuration for claims categories
  const componentInfoFormConfig: FormConfigProps = {
    initialValues: componentData,
    validationSchema: Yup.object().shape({
      scProductComponentCode: Yup.array()
        .of(
          Yup.string().test(
            "no-restricted-data",
            "NA, N/A, Not available, n/a, NA are not allowed",
            (value) => !RestrictedDataValidation.isRestrictedData(value)
          )
        )
        .min(1, "At least one component code is required"),
    }),
    readOnly: isReadOnlyBasedOnStatus,
    sections: [
      {
        // title: " Component Information",
        fields: [
          {
            name: "scProductComponentCode",
            label: "SC Product Component Code ",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 12 },
            helperText:
              'Please select "na" if Product Component Code is not applicable',
            events: {
              onChange: async (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                // Enforce mutual exclusivity: if 'na' is selected, it must be the only selection
                const values = Array.isArray(updatedValue) ? updatedValue : [];
                const hasNa = values.includes('na');
                let nextValues = values;

                if (hasNa && values.length > 1) {
                  nextValues = ['na'];
                } else if (!hasNa) {
                  nextValues = values.filter((v) => v !== 'na');
                }

                setComponentData(nextValues);
                setFieldValue('scProductComponentCode', nextValues);
              },
            },
            props: {
              enableSearch: true,
              placeholder: "Select SC Product Component Code",
              options: componentCodeOptions,
              value: componentData,
              multiple: true,
              isOptionDisabled: (option: any, currentValue: any) => {
                const selected = Array.isArray(currentValue) ? currentValue : [];
                const hasNaSelected = selected.includes('na');
                // If 'na' is selected, disable all others
                if (hasNaSelected) return option.value !== 'na';
                // If any non-'na' selected, disable 'na'
                if (!hasNaSelected && selected.length > 0 && option.value === 'na') return true;
                return false;
              },
              width: "600px",
            },
          },
        ],
      },
    ],
    pageName: "",
    handleSubmit: async (values: any) => {
      try {
        if (!componentData || _.isEmpty(componentData)) {
          notify("Please add component information details", "error");
          return false;
        }

        // Save the claims categories first
        const queryParams = {
          scId: SC_ID,
          scProductComponentCode: componentData.map((val: string) => {
            return componentCodeOptions?.find(
              (compCode) => compCode?.value === val
            )?.code;
          }),
        };

        const componentResult: any = await executePost(
          API_ROUTES.POST_COMPONENT_DETAILS_V4,
          queryParams
        );

        if (componentResult.status === 200 || componentResult.status === 201) {
          notify("SC Product Components saved successfully", "success");
          return true;
        } else {
          notify("Failed to save SC Product Components", "error");
          return false;
        }
      } catch (error) {
        console.error("Error saving SC Product Components:", error);
        notify("Error saving SC Product Components", "error");
        return false;
      }
    },
  };
  const handleDeleteComponent = (itemToDelete: any) => {
    setComponentData((prev) => prev.filter((item) => item !== itemToDelete));
  };
  const handleDeleteClaims = (itemToDelete: any) => {
    setClaimsCategoryData((prev) =>
      prev.filter((item) => item !== itemToDelete)
    );
  };

  return (
    <>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {/* Component Information Section */}
          <Box sx={{ mb: 4 }} width={"80vw"}>
            <Typography
              variant="subtitle1"
              gutterBottom
              align="left"
              fontWeight={"600"}
            >
              Component Information
            </Typography>

            <Box sx={{ border: "2px solid rgba(128, 128, 128, .2)", width:"85vw",padding : "2rem", borderRadius:2 }}>
              <FormBuilderV4
                formConfig={componentInfoFormConfig}
                onSubmit={componentInfoFormConfig?.handleSubmit}
                formRef={componentInfoFormRef}
              />

              <Box
                sx={{
                  maxHeight: 3 * 56, // 3 rows * chip height (approx 56px)
                  overflowY: "auto",
                  width: "80vw",
                  padding: 1,
                  borderRadius: 2,
                }}
              >
                <Grid container spacing={1}>
                  {componentData.map((item: any, index: number) => (
                    <Grid item xs={4} key={index}>
                      <Chip
                        label={item}
                        onDelete={() => handleDeleteComponent(item)}
                        deleteIcon={<Cancel />}
                        variant="outlined"
                        sx={{ borderRadius: "16px", width: "100%" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Box>
          {/* FormBuilderV4 for Claims Categories */}
          <Box sx={{ mb: 4 }} width={"80vw"}>
            <Typography
              variant="subtitle1"
              gutterBottom
              align="left"
              fontWeight={"600"}
            >
              SC Product Claims Category
            </Typography>
            <Box sx={{ border: "2px solid rgba(128, 128, 128, .2)", width:"85vw",padding : "2rem", borderRadius:2 }}>
              <FormBuilderV4
                formConfig={formConfig}
                onSubmit={formConfig.handleSubmit}
                formRef={formRef}
              />

              <Box
                sx={{
                  maxHeight: 3 * 56, // 3 rows * chip height (approx 56px)
                  overflowY: "auto",
                  width: "80vw",
                  padding: 1,
                  borderRadius: 2,
                }}
              >
                <Grid container spacing={1}>
                  {claimsCategoryData.map((item: any, index: number) => (
                    <Grid item xs={4} key={index}>
                      <Chip
                        label={item}
                        onDelete={() => handleDeleteClaims(item)}
                        deleteIcon={<Cancel />}
                        variant="outlined"
                        sx={{ borderRadius: "16px", width: "100%" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
            {/* Footer Action Buttons - following facility-product-details pattern */}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: "0.5rem",
            }}
          >
            <NavLink to="/v4/sc-dashboard">
              <InnerButton>Home</InnerButton>
            </NavLink>

            <InnerButton
              onClick={handleSCSave}
              // disabled={isReadOnlyBasedOnStatus}
            >
              Save
            </InnerButton>
            {(componentData?.length > 0 || claimsCategoryData?.length > 0) && (
              <>
                <InnerButton
                  onClick={handleSCSubmit}
                  // disabled={isReadOnlyBasedOnStatus}
                >
                  Submit
                </InnerButton>

                <Tooltip title={"Click to Preview"}>
                  <span>
                    <InnerButton onClick={togglePreviewDrawer}>
                      Preview
                    </InnerButton>
                  </span>
                </Tooltip>

                <Tooltip title={"Click to Download"}>
                  <span>
                    <InnerButton
                      onClick={handleDownload}
                      disabled={isDownloading}
                      sx={{
                        position: "relative",
                        "& .loading-indicator": {
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                        },
                      }}
                    >
                      {isDownloading ? (
                        <Box
                          className="loading-indicator"
                          display="flex"
                          alignItems="center"
                        >
                          <CircularProgress size={20} color="inherit" />
                        </Box>
                      ) : (
                        "Download Word"
                      )}
                    </InnerButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>

          {/* Preview Modal */}
          {SC_ID && (
            <ViewerModalPopup
              open={previewOpen}
              onClose={togglePreviewDrawer}
              title="Preview"
            >
              <div style={{ width: "100%", height: "100vh" }}>
                <PdfViewer
                  apiUrl={API_ROUTES.GET_PREVIEW_SC_V4}
                  params={{ scId: SC_ID }}
                />
              </div>
            </ViewerModalPopup>
          )}
        </>
      )}
    </>
  );
};

export default ComponentClaimsCategoryDetailsTab;
